import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, MoreHorizontal, Search, Camera, X } from "lucide-react";
import { PageHeader, Badge, EmptyState, priorityTone, statusTone } from "@/components/Common";
import { fileUrl } from "@/lib/api";

const CATEGORIES = ["finance", "procurement", "security", "customer_care", "operations", "it"];
const PRIORITIES = ["critical", "high", "medium", "low"];
const STATUSES = ["open", "in_progress", "escalated", "resolved", "closed"];

export default function Issues() {
  const [items, setItems] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", category: "operations", priority: "medium", branch: "",
  });
  const [files, setFiles] = useState([]);
  const [filter, setFilter] = useState({ status: "all", priority: "all", q: "" });
  const [busy, setBusy] = useState(false);
  const [viewAttachments, setViewAttachments] = useState(null); // {issue, index}

  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [{ data: issues }, { data: br }] = await Promise.all([
          api.get("/issues"),
          api.get("/branches"),
        ]);
        if (!cancelled) {
          setItems(issues);
          setBranches(br);
        }
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e.response?.data?.detail));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);
  const load = () => setRefreshKey((k) => k + 1);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.branch) payload.branch = branches[0]?.name || "";
      // Upload attachments first
      const fileIds = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        fileIds.push(data.id);
      }
      payload.attachments = fileIds;
      await api.post("/issues", payload);
      toast.success("Issue reported");
      setOpenNew(false);
      setForm({
        title: "", description: "", category: "operations", priority: "medium",
        branch: branches[0]?.name || "",
      });
      setFiles([]);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/issues/${id}`, { status });
      toast.success(`Marked ${status.replaceAll("_", " ")}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const filtered = items.filter((it) => {
    if (filter.status !== "all" && it.status !== filter.status) return false;
    if (filter.priority !== "all" && it.priority !== filter.priority) return false;
    if (filter.q && !`${it.title} ${it.description} ${it.branch}`.toLowerCase().includes(filter.q.toLowerCase()))
      return false;
    return true;
  });

  return (
    <div>
      <PageHeader
        eyebrow="Operations"
        title="Issue Reporting & Incidents"
        subtitle="Report, track and resolve operational, financial, security and IT incidents."
        actions={
          <Dialog open={openNew} onOpenChange={setOpenNew}>
            <DialogTrigger asChild>
              <Button
                className="bg-emerald-900 hover:bg-emerald-950 text-white"
                data-testid="new-issue-button"
              >
                <Plus size={16} className="mr-1.5" /> Report issue
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-display">Report a new issue</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="new-issue-form">
                <div>
                  <Label>Title</Label>
                  <Input
                    required
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="POS machine not booting at till 3"
                    data-testid="issue-title-input"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    required
                    rows={4}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="What happened, when, what is the impact?"
                    data-testid="issue-description-input"
                    className="mt-1"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Category</Label>
                    <Select
                      value={form.category}
                      onValueChange={(v) => setForm({ ...form, category: v })}
                    >
                      <SelectTrigger data-testid="issue-category-select" className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((c) => (
                          <SelectItem key={c} value={c}>
                            {c.replaceAll("_", " ")}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Priority</Label>
                    <Select
                      value={form.priority}
                      onValueChange={(v) => setForm({ ...form, priority: v })}
                    >
                      <SelectTrigger data-testid="issue-priority-select" className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PRIORITIES.map((p) => (
                          <SelectItem key={p} value={p}>{p}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Branch</Label>
                  <Select
                    value={form.branch}
                    onValueChange={(v) => setForm({ ...form, branch: v })}
                  >
                    <SelectTrigger data-testid="issue-branch-select" className="mt-1">
                      <SelectValue placeholder="Choose branch" />
                    </SelectTrigger>
                    <SelectContent>
                      {branches.map((b) => (
                        <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Photos (optional)</Label>
                  <Input type="file" multiple accept="image/png,image/jpeg,image/webp"
                    onChange={(e) => setFiles(Array.from(e.target.files || []))}
                    className="mt-1" data-testid="issue-files-input" />
                  {files.length > 0 && (
                    <div className="mt-1.5 text-xs text-gray-600">
                      {files.length} photo(s) selected: {files.map((f) => f.name).join(", ")}
                    </div>
                  )}
                  <p className="text-[11px] text-gray-400 mt-1">JPG / PNG / WEBP up to 8 MB each. Video &amp; voice attachments coming soon.</p>
                </div>
                <DialogFooter>
                  <Button
                    type="submit"
                    disabled={busy}
                    className="bg-emerald-900 hover:bg-emerald-950 text-white"
                    data-testid="submit-issue-button"
                  >
                    {busy ? "Submitting…" : "Submit issue"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-md p-3 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search issues…"
            value={filter.q}
            onChange={(e) => setFilter({ ...filter, q: e.target.value })}
            className="pl-8 h-9"
            data-testid="issue-search-input"
          />
        </div>
        <Select value={filter.status} onValueChange={(v) => setFilter({ ...filter, status: v })}>
          <SelectTrigger className="w-40 h-9" data-testid="filter-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s}>{s.replaceAll("_", " ")}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filter.priority} onValueChange={(v) => setFilter({ ...filter, priority: v })}>
          <SelectTrigger className="w-40 h-9" data-testid="filter-priority">
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All priorities</SelectItem>
            {PRIORITIES.map((p) => (
              <SelectItem key={p} value={p}>{p}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-gray-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <EmptyState
            title="No issues match your filters"
            hint="Report the first issue or change the filters above."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="issues-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left text-xs font-bold text-gray-600 uppercase tracking-wider px-4 py-3">Title</th>
                  <th className="text-left text-xs font-bold text-gray-600 uppercase tracking-wider px-4 py-3">Branch</th>
                  <th className="text-left text-xs font-bold text-gray-600 uppercase tracking-wider px-4 py-3">Category</th>
                  <th className="text-left text-xs font-bold text-gray-600 uppercase tracking-wider px-4 py-3">Priority</th>
                  <th className="text-left text-xs font-bold text-gray-600 uppercase tracking-wider px-4 py-3">Status</th>
                  <th className="text-left text-xs font-bold text-gray-600 uppercase tracking-wider px-4 py-3">Reported by</th>
                  <th className="text-left text-xs font-bold text-gray-600 uppercase tracking-wider px-4 py-3">Photos</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((i) => (
                  <tr key={i.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{i.title}</div>
                      <div className="text-xs text-gray-500 line-clamp-1 max-w-xs">{i.description}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{i.branch}</td>
                    <td className="px-4 py-3 text-gray-700">{i.category?.replaceAll("_", " ")}</td>
                    <td className="px-4 py-3"><Badge tone={priorityTone(i.priority)}>{i.priority}</Badge></td>
                    <td className="px-4 py-3"><Badge tone={statusTone(i.status)}>{(i.status || "open").replaceAll("_", " ")}</Badge></td>
                    <td className="px-4 py-3 text-gray-700">{i.created_by_name || "—"}</td>
                    <td className="px-4 py-3">
                      {i.attachments && i.attachments.length > 0 ? (
                        <button type="button"
                          onClick={() => setViewAttachments({ issue: i, index: 0 })}
                          className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-900 hover:bg-emerald-100 transition"
                          data-testid={`issue-photos-${i.id}`}>
                          <Camera size={12} /> {i.attachments.length}
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`issue-actions-${i.id}`}>
                            <MoreHorizontal size={16} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {STATUSES.map((s) => (
                            <DropdownMenuItem key={s} onClick={() => updateStatus(i.id, s)}>
                              Mark as {s.replaceAll("_", " ")}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Attachment lightbox */}
      {viewAttachments && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          data-testid="issue-attachment-lightbox"
          onClick={() => setViewAttachments(null)}>
          <button className="absolute top-4 right-4 h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            data-testid="issue-attachment-close"
            onClick={() => setViewAttachments(null)}>
            <X size={16} />
          </button>
          <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={fileUrl(viewAttachments.issue.attachments[viewAttachments.index])}
              alt=""
              className="max-h-[80vh] w-full object-contain rounded-lg"
              data-testid="issue-attachment-image"
            />
            {viewAttachments.issue.attachments.length > 1 && (
              <div className="flex justify-center gap-1.5 mt-3">
                {viewAttachments.issue.attachments.map((_, i) => (
                  <button key={i}
                    onClick={() => setViewAttachments({ ...viewAttachments, index: i })}
                    className={`h-2 rounded-full transition-all ${
                      i === viewAttachments.index ? "w-6 bg-amber-400" : "w-2 bg-white/40 hover:bg-white/70"
                    }`}
                    data-testid={`issue-attachment-dot-${i}`} />
                ))}
              </div>
            )}
            <div className="text-center text-white/80 text-sm mt-2">
              {viewAttachments.issue.title} — {viewAttachments.index + 1} / {viewAttachments.issue.attachments.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
